import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import useGoogleLoginEnabled from '../../hooks/useGoogleLoginEnabled';

export default function Register() {
    const [searchParams] = useSearchParams();
    // Tag d'acquisition interne (?from=) : slug court accepté tel quel, revalidé
    // côté backend. Capture toutes les campagnes (livre-qcm, landing-dgfip, ...).
    const fromParam = (searchParams.get('from') || '').toLowerCase();
    const acquisitionSource = /^[a-z0-9][a-z0-9_-]{0,62}$/.test(fromParam) ? fromParam : null;

    const [prenom, setPrenom] = useState('');
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [localError, setLocalError] = useState(null);
    const [registered, setRegistered] = useState(false);
    const { register, loginWithGoogle, markOnboarded, loading, error, clearError } = useAuthStore();
    const navigate = useNavigate();
    const googleEnabled = useGoogleLoginEnabled();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        if (password !== confirmPassword) {
            setLocalError('Les mots de passe ne correspondent pas');
            return;
        }

        const result = await register(email, password, prenom, acquisitionSource);
        if (result.success) {
            setRegistered(true);
        }
    };

    const handleGoogleCredential = async (credential) => {
        const result = await loginWithGoogle(credential, acquisitionSource);
        if (result.success) {
            // Nouvel inscrit Google → accueil guidé (affiché une seule fois).
            if (result.onboarded === false) {
                // Inscrit venu d'une landing : directement dans sa thématique (cf. Login).
                let landingScope = null;
                try { landingScope = (JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || '{}') || {}).landingScope || null; } catch {}
                if (landingScope) {
                    markOnboarded();
                    navigate(`/compte/quiz/${landingScope}`, { state: { fromAuth: true } });
                    return;
                }
                navigate('/compte/guide', { state: { fromAuth: true } });
                return;
            }
            const raw = searchParams.get('redirect');
            const safe = raw && raw.startsWith('/') && !raw.startsWith('//');
            navigate(safe ? raw : '/compte/quiz/mes-thematiques', { state: { fromAuth: true } });
        }
    };

    const displayError = localError || error;

    if (registered) {
        return (
            <div className="auth-page">
                <div className="auth-card">
                    <div className="auth-success-icon">&#9993;</div>
                    <h1>Vérifiez votre email</h1>
                    <p className="auth-success-text">
                        Un email d'activation a été envoyé à <strong>{email}</strong>.
                        <br />Cliquez sur le lien dans l'email pour activer votre compte.
                    </p>
                    <p className="auth-hint">
                        Pensez à vérifier vos spams si vous ne voyez pas l'email.
                    </p>
                    <Link to="/connexion" className="btn btn-primary">
                        Aller à la connexion
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card">
                <h1>Créer un compte</h1>

                {acquisitionSource === 'livre-qcm' && (
                    <p style={{ marginTop: -10, marginBottom: 16, fontSize: '0.85em', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center' }}>
                        Bienvenue, lecteur du livre. Tu pourras appliquer la méthode directement sur la plateforme.
                    </p>
                )}

                {displayError && (
                    <div className="alert alert-error">
                        {displayError}
                        <button onClick={() => { setLocalError(null); clearError(); }} className="alert-close">&times;</button>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="prenom">Pr&eacute;nom</label>
                        <input
                            id="prenom"
                            type="text"
                            value={prenom}
                            onChange={(e) => setPrenom(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>

                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Inscription...' : 'S\'inscrire'}
                    </button>
                </form>

                {googleEnabled && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>
                            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                            ou
                            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                        </div>
                        <GoogleSignInButton onCredential={handleGoogleCredential} text="signup_with" />
                    </>
                )}

                <p className="auth-link">
                    Déjà un compte ? <Link to="/connexion">Se connecter</Link>
                </p>
            </div>
        </div>
    );
}
